<template>
  <!--
    기하(슬롯 높이 · 시각 거터 폭 · 헤더 높이)는 **script 의 metrics 하나**가 정한다.
    예전에는 같은 값이 JS 상수(slotHeight=30, timeLabelsWidth=60, top 계산의 +80)와
    CSS 리터럴 양쪽에 따로 박혀 있어서, CSS 만 모바일용으로 줄이면 드래그로 만든
    일정이 실제로 놓인 시각과 어긋났다. 그래서 모바일 대응을 못 하고 있었다.
    이제 CSS 는 여기서 내려주는 변수를 읽고, JS 는 같은 metrics 를 읽는다.
  -->
  <div
    class="custom-time-grid"
    :class="{ 'week-view-mode': viewType === 'week', 'is-dragging': dragState.active }"
    :style="gridVars"
    ref="timeGridContainer"
  >
    <div class="time-grid-wrapper" ref="scrollWrapper">
      <!-- 시간 헤더 -->
      <div class="time-header">
        <div class="time-label-column"></div>
        <div class="date-columns-header">
          <div
            v-for="(date, colIndex) in dates"
            :key="date.toISOString()"
            class="date-column date-column-clickable"
            :class="{
              'is-today': isToday(date),
              'is-saturday': isSaturday(date),
              'is-sunday': isSunday(date),
              'is-holiday': isHoliday(date)
            }"
            role="button"
            tabindex="0"
            :aria-label="`${formatDate(date)} 종일 일정 추가`"
            @click="handleAllDayClick(date)"
            @keydown.enter.prevent="handleAllDayClick(date)"
            @keydown.space.prevent="handleAllDayClick(date)"
            :title="`${formatDate(date)} 종일 일정 추가`"
          >
            <div class="date-dow">{{ formatDay(date) }}</div>
            <div class="date-line">
              <!--
                주 뷰는 달을 넘긴다. 숫자만 있으면 "31, 1, 2" 가 어느 달인지 읽히지
                않아서 첫 컬럼과 1일에만 월을 붙인다. 원(26px) 안에 넣으면 넘치므로
                원 왼쪽에 따로 세운다. 전체 날짜는 컬럼 title 에 그대로 남아 있다.
              -->
              <span v-if="colIndex === 0 || date.getDate() === 1" class="date-mon u-num">
                {{ date.getMonth() + 1 }}월
              </span>
              <span class="date-num u-num">{{ date.getDate() }}</span>
            </div>
            <div v-if="getHolidayName(date)" class="holiday-name">
              {{ getHolidayName(date) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 종일 일정 영역 -->
      <div
        class="all-day-section"
        :class="{ 'is-tall': allDayTall }"
        ref="allDaySection"
      >
        <div class="all-day-label">
          <span class="all-day-label__text">종일</span>
          <!--
            줄이 접기 기준을 넘을 때만 나온다. 두 줄짜리 밴드에 토글이 붙어 있으면
            누를 이유가 없는 컨트롤이 화면에 상주한다.
          -->
          <button
            v-if="allDayRowCount > COLLAPSED_ROWS"
            type="button"
            class="all-day-toggle"
            :aria-expanded="!allDayCollapsed"
            :title="allDayCollapsed ? `종일 일정 ${allDayRowCount}줄 모두 보기` : '종일 영역 접기'"
            @click="toggleAllDay"
          >
            <span v-if="hiddenAllDayRows" class="all-day-toggle__more u-num">+{{ hiddenAllDayRows }}</span>
            <svg class="all-day-toggle__caret" :class="{ 'is-open': !allDayCollapsed }"
                 width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M6 9.5 12 15.5 18 9.5" />
            </svg>
          </button>
        </div>
        <div class="all-day-events-container">
          <!-- 통합 종일 이벤트 레이어 -->
          <div
            class="all-day-events-grid"
            :class="{
              'week-view': viewType === 'week',
              'day-view': viewType === 'day'
            }"
          >
            <!--
              한 줄짜리 칩이다. 예전에는 45px 높이에 [날짜범위][장소][담당자] 한 줄 +
              [제목] 한 줄로 두 줄을 썼는데, 종일 항목이 서너 개만 돼도 밴드가
              그리드 절반을 먹었다. 월 그리드의 스팬 칩과 같은 모양(한 줄, 24px)으로
              맞춘다 — 같은 정보가 두 뷰에서 다른 모양일 이유가 없다.
              단일일 항목의 날짜범위("8/24")는 그 컬럼이 이미 말해 주므로 접는다.
              (전체 범위는 title 에 남는다)
            -->
            <div
              v-for="eventInfo in visibleAllDayEvents"
              :key="eventInfo.event.id"
              class="all-day-event event-block"
              :class="[
                `event-${eventInfo.event.eventType || itemOf(eventInfo.event).eventType}`,
                itemOf(eventInfo.event).kind === 'task'
                  ? ['is-task', `task-${itemOf(eventInfo.event).status}`]
                  : 'is-schedule',
                {
                  'is-start': eventInfo.isStart,
                  'is-end': eventInfo.isEnd,
                  'is-multi-day': eventInfo.isMultiDay,
                  'is-selected': selectedEventId === eventInfo.event.id,
                  'is-past': isPast(eventInfo.event)
                }
              ]"
              :style="{
                gridColumn: `${eventInfo.startCol} / ${eventInfo.endCol + 1}`,
                gridRow: eventInfo.row + 1
              }"
              :title="`${eventInfo.event.title} · ${formatEventDateRange(eventInfo.event)}`"
              role="button"
              tabindex="0"
              @click="handleAllDayEventClick(eventInfo.event)"
              @keydown.enter.prevent="handleAllDayEventClick(eventInfo.event)"
              @keydown.space.prevent="handleAllDayEventClick(eventInfo.event)"
            >
              <span
                v-if="itemOf(eventInfo.event).kind === 'task'"
                class="event-check"
                :class="{ 'is-done': itemOf(eventInfo.event).status === 'done' }"
                aria-hidden="true"
              ></span>
              <span v-if="eventInfo.isMultiDay" class="event-date-range u-num">
                {{ formatEventDateRange(eventInfo.event) }}
              </span>
              <span class="event-title">{{ eventInfo.event.title }}</span>
              <span v-if="locationOf(eventInfo.event)" class="location-badge">
                <span class="location-icon"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z"/><circle cx="12" cy="10" r="2.6"/></svg></span>
                <span class="location-text">{{ locationOf(eventInfo.event) }}</span>
              </span>
              <span v-if="userOf(eventInfo.event)" class="event-user">
                <img v-if="getUserProfileImage(userOf(eventInfo.event))"
                    :src="getUserProfileImage(userOf(eventInfo.event))"
                    class="user-avatar" alt="" />
                <span class="user-name">{{ userOf(eventInfo.event).nickname }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 시간 그리드 본체 -->
      <div class="time-grid-body" ref="gridBody">
        <!-- 시간 레이블 -->
        <div class="time-labels">
          <div
            v-for="hour in hours"
            :key="hour"
            class="time-label-wrapper"
          >
            <div class="time-label-text u-num">{{ formatHour(hour) }}</div>
          </div>

          <!--
            현재 시각은 거터에 숫자로도 찍는다. 빨간 선만 있으면 "지금이 몇 시인지" 를
            눈으로 시간 눈금과 맞춰 세야 한다. 홈·월 그리드와 같은 모양이다.
            오늘이 이 뷰에 없으면(지난 주를 보는 중) 선도 숫자도 그리지 않는다.
          -->
          <div
            v-if="showsToday"
            class="now-label u-num"
            :style="{ top: `${currentTimePosition}px` }"
          >{{ nowLabel }}</div>
        </div>

        <!-- 날짜별 컬럼 -->
        <div class="date-columns" ref="dateColumnsRef">
          <div
            v-for="(date, dateIndex) in dates"
            :key="date.toISOString()"
            class="date-column-body"
            :class="{ 
              'is-today': isToday(date),
              'is-saturday': isSaturday(date),
              'is-sunday': isSunday(date),
              'is-holiday': isHoliday(date)
            }"
          >
            <!-- 현재 시간 표시선 -->
            <div
              v-if="isToday(date)"
              class="current-time-indicator"
              :style="{ top: `${currentTimePosition}px` }"
            >
              <div class="current-time-line"></div>
              <div class="current-time-dot"></div>
            </div>

            <!-- 시간 슬롯 그리드 -->
            <div class="time-slots-container">
              <div
                v-for="(slot, slotIndex) in timeSlots"
                :key="`${dateIndex}-${slotIndex}`"
                class="time-slot"
                :data-slot-index="slotIndex"
                :data-date-index="dateIndex"
                @mousedown="handleSlotMouseDown($event, date, slotIndex)"
              >
                <div class="slot-divider" v-if="slotIndex % 2 === 1"></div>
              </div>
            </div>

            <!-- 이벤트 컨테이너 -->
            <div class="events-container">
              <!--
                제목이 먼저다. 예전에는 시각이 첫 줄이었는데, 블록의 세로 위치가
                이미 시각을 말하고 있어서 30분짜리 블록에서는 정작 제목이 잘렸다.
                (제목 한 줄 → 시각+장소 한 줄 → 담당자. 뒤의 두 줄은 블록이
                 높을 때만 보인다 — overflow:hidden 이 알아서 자른다)
              -->
              <div
                v-for="layout in getEventLayouts(date)"
                :key="layout.event.id"
                class="event-block"
                :class="[
                  `event-${layout.event.eventType || itemOf(layout.event).eventType}`,
                  itemOf(layout.event).kind === 'task'
                    ? ['is-task', `task-${itemOf(layout.event).status}`]
                    : 'is-schedule',
                  {
                    'is-draggable': itemOf(layout.event).canEdit !== false,
                    'is-selected': selectedEventId === layout.event.id,
                    'is-past': isPast(layout.event)
                  }
                ]"
                :style="layout.style"
                :title="`${layout.event.title} ${formatEventTime(layout.event)}`"
                @mousedown.stop="handleEventMouseDown($event, layout.event)"
              >
                <div class="event-content">
                  <div class="event-title-row">
                    <span
                      v-if="itemOf(layout.event).kind === 'task'"
                      class="event-check"
                      :class="{ 'is-done': itemOf(layout.event).status === 'done' }"
                      aria-hidden="true"
                    ></span>
                    <span class="event-title">{{ layout.event.title }}</span>
                  </div>
                  <div class="event-time">
                    <span class="event-time-text u-num">{{ formatEventTime(layout.event) }}</span>
                    <span v-if="locationOf(layout.event)" class="location-badge">
                      <span class="location-icon"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z"/><circle cx="12" cy="10" r="2.6"/></svg></span>
                      <span class="location-text">{{ locationOf(layout.event) }}</span>
                    </span>
                  </div>
                  <div v-if="userOf(layout.event) && layout.showUser" class="event-user">
                    <img v-if="getUserProfileImage(userOf(layout.event))"
                        :src="getUserProfileImage(userOf(layout.event))"
                        class="user-avatar" alt="" />
                    <span class="user-name">{{ userOf(layout.event).nickname }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!--
        드래그 미리보기 오버레이.
        **스크롤 래퍼 안**에 있어야 한다. dragOverlayStyle 의 top/left 는
        "그리드 콘텐츠 좌표"(헤더 0 부터 잰 절대 위치)인데, 예전에는 이 div 가
        래퍼 밖에 있어서 스크롤한 만큼 통째로 어긋났다. 하루가 1248~1440px 이고
        이제는 처음부터 08:00 로 내려놓으므로 항상 어긋난 상태였다.
        래퍼가 position:relative 이므로 여기 두면 좌표가 그대로 맞는다.
      -->
      <div
        v-if="dragState.active"
        class="drag-overlay"
        :style="dragOverlayStyle"
      >
        <div class="drag-overlay-content">
          {{ dragOverlayText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useHolidays } from '@/composables/useHolidays'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { getProfileImageUrl } from '../utils/profileUtils'
import { ymd } from '@/utils/agendaItem'

export default {
  name: 'CustomTimeGrid',
  props: {
    events: {
      type: Array,
      default: () => []
    },
    viewType: {
      type: String,
      default: 'week'
    },
    currentDate: {
      type: Date,
      required: true
    },
    accessibleUsers: {
      type: Array,
      default: () => []
    }
  },
  emits: ['event-click', 'event-create', 'event-update'],
  setup(props, { emit }) {
    const timeGridContainer = ref(null)
    const scrollWrapper = ref(null)
    const allDaySection = ref(null)
    const gridBody = ref(null)
    const dateColumnsRef = ref(null)
    /*
     * 화면 폭에 따른 기하. 모바일에서 데스크톱 값을 그대로 쓰면
     * 시각 거터가 화면 폭의 15%를 먹고 하루가 1440px 이라 스크롤이 끝나지 않는다.
     * 슬롯을 26px 로 줄이면 하루가 1248px 이 되고 거터도 46px 로 내려간다.
     */
    const { isMobile: tgIsMobile } = useBreakpoint()
    const metrics = computed(() => (tgIsMobile.value
      ? { slot: 26, gutter: 46, header: 60 }
      : { slot: 30, gutter: 60, header: 80 }))

    // CSS 는 이 변수들을 읽는다. JS 와 CSS 가 같은 숫자를 보게 만드는 유일한 연결점이다.
    const gridVars = computed(() => ({
      '--tg-slot-h': `${metrics.value.slot}px`,
      '--tg-gutter': `${metrics.value.gutter}px`,
      '--tg-header-h': `${metrics.value.header}px`,
      // 하루 = 30분 슬롯 48개
      '--tg-day-h': `${metrics.value.slot * 48}px`
    }))

    const slotHeight = computed(() => metrics.value.slot)
    const selectedEventId = ref(null)
    const currentTime = ref(new Date())

    // 드래그 상태
    const IDLE_DRAG = () => ({
      active: false,
      type: null,
      startSlotIndex: -1,
      endSlotIndex: -1,
      dateIndex: -1,
      originalEvent: null,
      duration: 0,
      originalDateIndex: -1,
      originStartSlot: -1,
      clickOffset: 0
    })
    const dragState = ref(IDLE_DRAG())

    /*
     * handleSyncScroll 은 삭제했다.
     * 헤더·종일·본문 컬럼은 모두 실제 스크롤러(.time-grid-wrapper)의 **비스크롤**
     * 자식이라 scrollLeft 를 대입해도 아무 일이 일어나지 않았고, allDayColumns 는
     * 템플릿에 ref 바인딩조차 없어 항상 null 이었다. 셋이 한 스크롤러 안에 함께
     * 들어 있어서 동기화가 애초에 필요 없다.
     */

    // 24시간 배열
    const hours = computed(() => {
      return Array.from({ length: 24 }, (_, i) => i)
    })

    // 시간 슬롯 (30분 단위, 48개)
    const timeSlots = computed(() => {
      return Array.from({ length: 48 }, (_, i) => i)
    })

    // 날짜 배열
    const dates = computed(() => {
      if (props.viewType === 'day') {
        return [new Date(props.currentDate)]
      } else {
        const startOfWeek = new Date(props.currentDate)
        const day = startOfWeek.getDay()
        const diff = startOfWeek.getDate() - day
        startOfWeek.setDate(diff)
        startOfWeek.setHours(0, 0, 0, 0)

        const week = []
        for (let i = 0; i < 7; i++) {
          const date = new Date(startOfWeek)
          date.setDate(startOfWeek.getDate() + i)
          week.push(date)
        }
        return week
      }
    })

    // 연말·연초 주는 두 해에 걸친다(12/28 주의 1월 1일). 첫날과 마지막 날의 해를 모두 받는다.
    const holidayYears = computed(() => {
      const d = dates.value
      return [d[0].getFullYear(), d[d.length - 1].getFullYear()]
    })
    const { isHoliday: isHolidayYmd, holidayName: holidayNameYmd } = useHolidays(holidayYears)

    // 현재 시간 위치
    const currentTimePosition = computed(() => {
      const now = currentTime.value
      const minutes = now.getHours() * 60 + now.getMinutes()
      return (minutes / 30) * slotHeight.value
    })

    /*
     * 거터에 찍는 현재 시각 문자열과, 그걸 그릴지 여부.
     * 인디케이터 선은 컬럼마다 isToday 로 판단하는데 거터는 컬럼이 아니라서
     * "이 뷰에 오늘이 있나" 를 따로 물어야 한다. 값은 선과 같은 currentTime 에서
     * 나오므로 숫자와 선이 갈라질 일이 없다.
     */
    const nowLabel = computed(() => {
      const d = currentTime.value
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    })
    const showsToday = computed(() => dates.value.some((d) => isToday(d)))

    // 날짜 체크 함수들
    const isToday = (date) => {
      const today = new Date()
      return date.toDateString() === today.toDateString()
    }

    const isSaturday = (date) => {
      return date.getDay() === 6
    }

    const isSunday = (date) => {
      return date.getDay() === 0
    }

    const isHoliday = (date) => isHolidayYmd(ymd(date))
    const getHolidayName = (date) => holidayNameYmd(ymd(date)) || null

    // 종일 섹션 높이 계산
    const getAllDaySectionHeight = () => {
      // document.querySelector 로 전역 조회하면 한 화면에 그리드가 둘일 때
      // 남의 밴드를 잰다. ref 가 바로 옆에 있다.
      return allDaySection.value?.offsetHeight ?? 60
    }

    /*
     * getSlotFromMouseY / getDateIndexFromMouseX 는 삭제했다.
     *
     * 둘 다 호출부가 0 이었고, getSlotFromMouseY 는 계산도 틀려 있었다 —
     * gridBody(=.time-grid-body)는 이미 헤더와 종일 밴드 **아래**에서 시작하는데
     * 그 rect 기준 좌표에서 header + allDayHeight 를 또 빼고 있었다.
     * 누가 부르는 순간 140px 쯤(슬롯 네 칸 이상) 어긋난다.
     * 실제로 쓰이는 좌표 변환은 아래 dragOverlayStyle 과 각 컬럼의 포인터
     * 핸들러에 있다.
     */

    // 드래그 오버레이 스타일
    const dragOverlayStyle = computed(() => {
      if (!dragState.value.active || !gridBody.value || !dateColumnsRef.value) return {}

      const startSlot = Math.min(dragState.value.startSlotIndex, dragState.value.endSlotIndex)
      const endSlot = Math.max(dragState.value.startSlotIndex, dragState.value.endSlotIndex)
      const dateIndex = dragState.value.dateIndex

      if (dateIndex < 0 || startSlot < 0) return {}

      const gridRect = gridBody.value.getBoundingClientRect()
      const columnsRect = dateColumnsRef.value.getBoundingClientRect()
      
      const timeLabelsWidth = metrics.value.gutter
      const columnWidth = columnsRect.width / dates.value.length
      const allDayHeight = getAllDaySectionHeight()
      const top = startSlot * slotHeight.value + metrics.value.header + allDayHeight
      const height = (endSlot - startSlot + 1) * slotHeight.value
      const left = timeLabelsWidth + (dateIndex * columnWidth)

      return {
        position: 'absolute',
        top: `${top}px`,
        height: `${height}px`,
        left: `${left}px`,
        width: `${columnWidth}px`,
        // 그리드 안 최상단. 헤더(--tg-z-header)보다 위여야 끌고 있는 것이 가려지지 않는다.
        zIndex: 'var(--tg-z-drag)'
      }
    })

    // 드래그 오버레이 텍스트
    const dragOverlayText = computed(() => {
      if (!dragState.value.active) return ''

      const startSlot = Math.min(dragState.value.startSlotIndex, dragState.value.endSlotIndex)
      const endSlot = Math.max(dragState.value.startSlotIndex, dragState.value.endSlotIndex)

      const startHour = Math.floor(startSlot / 2)
      const startMin = (startSlot % 2) * 30
      const endHour = Math.floor((endSlot + 1) / 2)
      const endMin = ((endSlot + 1) % 2) * 30

      const startTime = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`
      const endTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`

      if (dragState.value.type === 'create') {
        return `새 일정 ${startTime} - ${endTime}`
      } else if (dragState.value.type === 'move') {
        return `${dragState.value.originalEvent?.title || '일정'} 이동`
      }
      return ''
    })

    // 특정 날짜의 시간 이벤트 필터링
    const getEventsForDate = (date) => {
      if (!date || !props.events || !Array.isArray(props.events)) return []

      const targetStart = new Date(date)
      targetStart.setHours(0, 0, 0, 0)
      const targetEnd = new Date(date)
      targetEnd.setHours(23, 59, 59, 999)

      return props.events.filter(event => {
        if (event.allDay || event.isAllDay) return false
        if (!event || (!event.start && !event.startDate)) return false

        try {
          const eventStart = new Date(event.start || event.startDate)
          const eventEnd = new Date(event.end || event.endDate || event.start || event.startDate)

          if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) return false

          // 이벤트가 해당 날짜와 겹치는지 확인 (여러 날 이벤트 포함)
          return eventStart <= targetEnd && eventEnd >= targetStart
        } catch (error) {
          console.warn('날짜 파싱 오류:', event, error)
          return false
        }
      }).map(event => {
        // 원본 이벤트 정보 보존
        const originalEvent = { ...event }
        const eventStart = new Date(event.start || event.startDate)
        const eventEnd = new Date(event.end || event.endDate || event.start || event.startDate)

        // 해당 날짜 범위
        const dayStart = new Date(date)
        dayStart.setHours(0, 0, 0, 0)
        const dayEnd = new Date(date)
        dayEnd.setHours(23, 59, 59, 999)

        // 여러 날짜에 걸친 이벤트인지 확인
        const isMultiDay = eventStart.toDateString() !== eventEnd.toDateString()

        if (isMultiDay) {
          // 여러 날짜 이벤트의 경우 해당 날짜에 맞게 시간 조정
          let displayStart = eventStart
          let displayEnd = eventEnd

          // 이벤트 시작이 해당 날짜보다 이전이면 그 날 00:00으로 설정
          if (eventStart < dayStart) {
            displayStart = new Date(dayStart)
          }

          // 이벤트 끝이 해당 날짜보다 이후면 그 날 23:59로 설정
          if (eventEnd > dayEnd) {
            displayEnd = new Date(dayEnd)
          }

          return {
            ...originalEvent,
            start: displayStart,
            end: displayEnd,
            originalStart: eventStart,
            originalEnd: eventEnd,
            isMultiDay: true,
            isStartDay: eventStart.toDateString() === date.toDateString(),
            isEndDay: eventEnd.toDateString() === date.toDateString()
          }
        }

        return originalEvent
      })
    }

    // 이벤트 레이아웃 계산
    const getEventLayouts = (date) => {
      const events = getEventsForDate(date)
      const layouts = []
      
      events.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())

      const groups = []
      let currentGroup = []
      let groupEnd = 0

      events.forEach(event => {
        const eventStart = new Date(event.start).getTime()
        const eventEnd = new Date(event.end).getTime()

        if (currentGroup.length === 0 || eventStart >= groupEnd) {
          if (currentGroup.length > 0) {
            groups.push(currentGroup)
          }
          currentGroup = [event]
          groupEnd = eventEnd
        } else {
          currentGroup.push(event)
          groupEnd = Math.max(groupEnd, eventEnd)
        }
      })
      if (currentGroup.length > 0) {
        groups.push(currentGroup)
      }

      groups.forEach(group => {
        const columns = []
        
        group.forEach(event => {
          let columnIndex = 0
          let placed = false

          while (!placed) {
            if (!columns[columnIndex]) {
              columns[columnIndex] = []
            }

            const canPlace = columns[columnIndex].every(existingEvent => {
              const eventStart = new Date(event.start).getTime()
              const eventEnd = new Date(event.end).getTime()
              const existingStart = new Date(existingEvent.start).getTime()
              const existingEnd = new Date(existingEvent.end).getTime()
              
              return eventStart >= existingEnd || eventEnd <= existingStart
            })

            if (canPlace) {
              columns[columnIndex].push(event)
              placed = true

              const startTime = new Date(event.start)
              const endTime = new Date(event.end)
              const startMinutes = startTime.getHours() * 60 + startTime.getMinutes()
              const endMinutes = endTime.getHours() * 60 + endTime.getMinutes()

              const top = (startMinutes / 30) * slotHeight.value
              const height = Math.max(((endMinutes - startMinutes) / 30) * slotHeight.value, 25)
              
              const totalColumns = columns.length
              const width = totalColumns > 0 ? `${95 / totalColumns}%` : '95%'
              const left = `${(columnIndex * 95) / totalColumns}%`

              layouts.push({
                event,
                style: {
                  position: 'absolute',
                  top: `${top}px`,
                  height: `${height}px`,
                  left: left,
                  width: width,
                  zIndex: 'var(--tg-z-block)'
                },
                showUser: height > 50
              })
            } else {
              columnIndex++
            }
          }
        })
      })

      return layouts
    }

    // 종일 이벤트 행 높이 계산

    /*
     * getAllDayRowsHeight 는 삭제했다.
     * `allDayRowsCount * 42` 로 행 높이를 두 번째로 정의하고 있었는데, 실제 높이는
     * CSS 의 --tg-allday-row(데스크톱 24px · 모바일 44px)라 어느 쪽으로도 42가 아니다.
     * 호출부가 없어 무해했지만, 기하를 metrics 한 곳으로 모은 이번 작업이
     * 없애려던 바로 그 종류의 이중 소스다. 밴드 높이가 필요하면
     * getAllDaySectionHeight() 가 offsetHeight 로 실측한다.
     */

    /*
     * 종일 밴드에서 이 항목이 앉을 줄의 키.
     * 담당자가 없는 항목(미배정 업무 등)도 줄을 받아야 한다 — 예전에는 id 가
     * falsy 면 집계에서 빠지고, 그 결과 행을 못 받아 화면에서 사라졌다.
     */
    const NO_OWNER = '__none__'
    const ownerKeyOf = (event) => {
      const id = event.userId || event.extendedProps?.user?.id || event.user?.id || userOf(event)?.id
      return id != null ? id : NO_OWNER
    }

    // 모든 종일 이벤트 처리 (유저 가나다순)
    const allDayLayout = computed(() => {
      // 현재 뷰 범위 계산
      const viewStart = new Date(dates.value[0])
      viewStart.setHours(0, 0, 0, 0)
      const viewEnd = new Date(dates.value[dates.value.length - 1])
      viewEnd.setHours(23, 59, 59, 999)

      // 1. 현재 뷰 범위에서 종일 이벤트가 있는 유저만 필터링
      //    (key → 정렬용 닉네임. 담당자가 없는 항목도 자기 줄이 필요하다)
      const usersWithEventsThisView = new Map()
      const allDayEvents = props.events.filter(e => e.allDay || e.isAllDay)

      allDayEvents.forEach(event => {
        const eventStart = new Date(event.start || event.startDate)
        const eventEnd = new Date(event.end || event.endDate)
        eventStart.setHours(0, 0, 0, 0)
        eventEnd.setHours(23, 59, 59, 999)

        // 이벤트가 현재 뷰와 겹치는지 확인
        if (eventStart <= viewEnd && eventEnd >= viewStart) {
          usersWithEventsThisView.set(ownerKeyOf(event), userOf(event)?.nickname || '')
        }
      })

      /*
       * 2~3. 행 순서를 정한다.
       *
       * 여기가 **종일 밴드가 통째로 비어 있던 자리**다.
       * 예전에는 accessibleUsers(prop)에 있는 사람만 행을 받았고, 행을 못 받은
       * 항목은 아래에서 `userBaseRow !== undefined` 에 걸려 조용히 버려졌다.
       * 그런데 Calendar.vue 는 이 prop 을 넘기지 않는다(기본 []). 결과적으로
       * 맵이 항상 비어서 종일 일정도, 종일로 들어오는 업무도 주/일 뷰에서
       * 하나도 보이지 않았다 — 밴드는 있는데 내용이 없었다.
       *
       * prop 이 오면 그 순서를 먼저 존중하고(부서 정렬 등 호출자의 의도),
       * prop 에 없는 사람과 담당자 없는 항목은 뒤에 닉네임순으로 붙인다.
       * 어느 쪽이든 **버리지 않는다.**
       */
      const byNick = (a, b) => String(a).localeCompare(String(b), 'ko')
      const order = []
      const pushKey = (key) => {
        if (usersWithEventsThisView.has(key) && !order.includes(key)) order.push(key)
      }
      ;[...props.accessibleUsers]
        .sort((a, b) => byNick(a.nickname, b.nickname))
        .forEach(user => pushKey(user.id))
      ;[...usersWithEventsThisView.entries()]
        .sort((a, b) => byNick(a[1], b[1]))
        .forEach(([key]) => pushKey(key))

      const userRowMap = new Map()
      order.forEach((key, index) => {
        userRowMap.set(key, index)
      })

      const allEvents = []

      // 4. 모든 종일 이벤트 처리
      allDayEvents.forEach(event => {
        const eventStart = new Date(event.start || event.startDate)
        const eventEnd = new Date(event.end || event.endDate)
        eventStart.setHours(0, 0, 0, 0)
        eventEnd.setHours(23, 59, 59, 999)

        // 이벤트가 현재 뷰와 겹치는지 확인
        if (eventStart <= viewEnd && eventEnd >= viewStart) {
          let startCol = -1
          let endCol = -1

          // 실제 표시할 시작/끝 날짜 계산
          const displayStart = eventStart < viewStart ? viewStart : eventStart
          const displayEnd = eventEnd > viewEnd ? viewEnd : eventEnd

          dates.value.forEach((date, index) => {
            const dayStart = new Date(date)
            dayStart.setHours(0, 0, 0, 0)
            const dayEnd = new Date(date)
            dayEnd.setHours(23, 59, 59, 999)

            if (displayStart <= dayEnd && displayEnd >= dayStart) {
              if (startCol === -1) startCol = index + 1
              endCol = index + 1
            }
          })

          if (startCol !== -1) {
            const isMultiDay = !isSameDay(eventStart, eventEnd)
            const userId = ownerKeyOf(event)
            const userBaseRow = userRowMap.get(userId)

            // 현재 뷰에서 이벤트가 있는 유저만 추가
            if (userBaseRow !== undefined) {
              allEvents.push({
                event,
                startCol,
                endCol,
                isStart: eventStart >= viewStart,
                isEnd: eventEnd <= viewEnd,
                isMultiDay: isMultiDay,
                row: 0,
                userBaseRow: userBaseRow,
                userId: userId
              })
            }
          }
        }
      })

      // 5. 이벤트를 유저별로 그룹화
      const userGroups = new Map()
      allEvents.forEach(eventInfo => {
        if (!userGroups.has(eventInfo.userBaseRow)) {
          userGroups.set(eventInfo.userBaseRow, [])
        }
        userGroups.get(eventInfo.userBaseRow).push(eventInfo)
      })

      // 6. 각 유저 그룹 내에서 이벤트 정렬 (시작 날짜순)
      userGroups.forEach(events => {
        events.sort((a, b) => {
          const aStart = new Date(a.event.start || a.event.startDate)
          const bStart = new Date(b.event.start || b.event.startDate)
          return aStart - bStart
        })
      })

      // 7. 유저별로 행 배치 (각 유저는 자신의 시작 행부터 사용)
      const allRows = []
      userGroups.forEach((events, userBaseRow) => {
        const userRows = []

        events.forEach(eventInfo => {
          let placed = false
          for (let offset = 0; !placed; offset++) {
            const rowIndex = userBaseRow + offset

            if (!allRows[rowIndex]) allRows[rowIndex] = []
            if (!userRows[offset]) userRows[offset] = []

            const canPlace = allRows[rowIndex].every(existing => {
              return eventInfo.startCol > existing.endCol ||
                    eventInfo.endCol < existing.startCol
            })

            if (canPlace) {
              eventInfo.row = rowIndex
              allRows[rowIndex].push(eventInfo)
              userRows[offset].push(eventInfo)
              placed = true
            }
          }
        })
      })

      return allEvents
    })

    /*
     * ── 종일 밴드를 몇 줄까지 펼쳐 둘 것인가 ──
     *
     * 예전에는 컨테이너에 `max-height: 30vh; overflow-y: auto` 를 걸어 두고
     * 넘치면 **밴드 안에서 따로 스크롤**하게 했다. 실제 데이터에서 이 조직의
     * 종일 밴드는 한 주에 중앙값 14줄, 많은 주는 24줄까지 나온다. 30vh 는
     * 대략 9줄이라, 평소에도 3분의 1이 작은 스크롤 상자 뒤에 숨어 있었다.
     * 스크롤바가 두 개(페이지 + 밴드) 겹치는 자리라 있는 줄도 못 찾는다.
     *
     * 이제 기본은 **전부 펼침**이다. 안쪽 스크롤은 없앤다.
     * 대신 두 가지를 둔다:
     *   · 접기 토글 — 밴드가 화면을 다 먹을 때 시간 그리드로 바로 갈 수 있게
     *   · 길어지면 sticky 해제 — 20줄짜리 밴드가 고정된 채 시간 그리드를
     *     덮어 버리면 밴드를 접기 전에는 아무것도 못 본다
     */
    const allDayCollapsed = ref(false)
    const COLLAPSED_ROWS = computed(() => (tgIsMobile.value ? 2 : 4))

    const allDayRowCount = computed(() =>
      allDayLayout.value.reduce((max, e) => Math.max(max, e.row + 1), 0)
    )

    /** 접혀 있으면 앞쪽 몇 줄만 렌더한다(숨긴 줄은 DOM 에도 두지 않는다) */
    const visibleAllDayEvents = computed(() =>
      allDayCollapsed.value
        ? allDayLayout.value.filter((e) => e.row < COLLAPSED_ROWS.value)
        : allDayLayout.value
    )

    const hiddenAllDayRows = computed(() =>
      allDayCollapsed.value ? Math.max(0, allDayRowCount.value - COLLAPSED_ROWS.value) : 0
    )

    /*
     * 밴드가 이 이상 길어지면 고정을 푼다.
     * 헤더(--tg-header-h)까지 붙어 있는 sticky 블록이 화면의 절반을 넘어가면
     * 시간 그리드가 보일 자리가 없다. 그 지점부터는 밴드가 함께 흘러가는 편이 낫다.
     */
    const STICKY_MAX_ROWS = computed(() => (tgIsMobile.value ? 3 : 8))
    const allDayTall = computed(() =>
      !allDayCollapsed.value && allDayRowCount.value > STICKY_MAX_ROWS.value
    )

    function toggleAllDay() { allDayCollapsed.value = !allDayCollapsed.value }

    // 포맷팅 함수들
    const formatDate = (date) => {
      return `${date.getMonth() + 1}/${date.getDate()}`
    }

    const formatDay = (date) => {
      const days = ['일', '월', '화', '수', '목', '금', '토']
      return days[date.getDay()]
    }

    const formatHour = (hour) => {
      return `${hour.toString().padStart(2, '0')}시`
    }

    const formatEventTime = (event) => {
      if (event.allDay) return '종일'

      const start = new Date(event.start)
      const end = new Date(event.end)
      const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`
      const endTime = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`

      // 여러 날짜에 걸친 이벤트인 경우
      if (event.isMultiDay) {
        if (event.isStartDay && !event.isEndDay) {
          // 시작일인 경우: "14:00~"
          return `${startTime}~`
        } else if (!event.isStartDay && event.isEndDay) {
          // 종료일인 경우: "~16:00"
          return `~${endTime}`
        } else if (!event.isStartDay && !event.isEndDay) {
          // 중간일인 경우: "하루종일"
          return '하루종일'
        }
      }

      return `${startTime}~${endTime}`
    }

    const formatEventDateRange = (event) => {
      const start = new Date(event.start || event.startDate)
      const end = new Date(event.end || event.endDate)

      const startDate = `${start.getMonth() + 1}/${start.getDate()}`
      const endDate = `${end.getMonth() + 1}/${end.getDate()}`

      if (isSameDay(start, end)) {
        return startDate
      } else {
        return `${startDate} ~ ${endDate}`
      }
    }

    const getGridPosition = (clientX, clientY) => {
      const element = document.elementFromPoint(clientX, clientY)
      if (!element) return { slotIndex: -1, dateIndex: -1 }
      
      let timeSlot = element.closest('.time-slot')
      let dateColumn = element.closest('.date-column-body')
      
      if (timeSlot && dateColumn) {
        const slotIndex = parseInt(timeSlot.dataset.slotIndex) || 0
        const dateIndex = parseInt(timeSlot.dataset.dateIndex) || 0
        
        return { slotIndex, dateIndex }
      }
      
      if (dateColumn && dateColumnsRef.value) {
        const columns = Array.from(dateColumnsRef.value.querySelectorAll('.date-column-body'))
        const dateIndex = columns.indexOf(dateColumn)
        
        const columnRect = dateColumn.getBoundingClientRect()
        const relativeY = clientY - columnRect.top
        const slotIndex = Math.floor(Math.max(0, relativeY) / slotHeight.value)
        
        return {
          slotIndex: Math.max(0, Math.min(47, slotIndex)),
          dateIndex: Math.max(0, Math.min(dates.value.length - 1, dateIndex))
        }
      }
      
      return { slotIndex: -1, dateIndex: -1 }
    }

    const slotToTime = (slotIndex) => {
      const hour = Math.floor(slotIndex / 2)
      const minute = (slotIndex % 2) * 30
      return { hour, minute }
    }

    const timeToSlot = (hour, minute) => {
      return hour * 2 + Math.floor(minute / 30)
    }

    const getUserProfileImage = (user) => {
      return getProfileImageUrl(user)
    }

    /* ────────────────── 표시용 접근자 ──────────────────
     * 계산이 아니라 **꺼내기**다. CalendarTimeGrid 가 넘기는 레거시 이벤트는
     * 정규화된 AgendaItem 을 extendedProps.agendaItem 에 담아 오는데,
     * 템플릿이 그 경로를 매번 풀어 쓰면 읽을 수가 없다.
     *
     * 여기 있는 이유는 그것만이 아니다. 예전 템플릿은 extendedProps.eventType /
     * .location / .user 를 **직접** 읽었는데 어댑터는 그 자리에 아무것도 넣지 않는다.
     * 그래서 시간 블록의 유형색 클래스가 `event-undefined` 로 붙어 **색이 아예 없었고**
     * (배경 없는 블록에 흰 글자), 장소·담당자는 영영 뜨지 않았다.
     * 레거시 위치도 폴백으로 남겨 둔다 — 다른 호출자가 생겨도 깨지지 않는다.
     */
    const itemOf = (event) => event?.extendedProps?.agendaItem || event?.extendedProps || event || {}
    const userOf = (event) => event?.user || event?.extendedProps?.user || itemOf(event).owner || null
    const locationOf = (event) =>
      itemOf(event).location?.name ||
      itemOf(event).locationCustom ||
      event?.extendedProps?.location?.name ||
      event?.extendedProps?.locationCustom ||
      ''
    /** 이미 끝난 항목은 흐리게 — 오늘 컬럼에서 "남은 일" 만 눈에 들어오게 한다 */
    const isPast = (event) => {
      const end = event?.end || event?.endDate
      return end ? new Date(end) < currentTime.value : false
    }

    // 종일 칸 클릭 핸들러
    const handleAllDayEventClick = (event) => {
      emit('event-click', event)
    }

    const handleAllDayClick = (date) => {
      const startDate = new Date(date)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(date)
      endDate.setHours(23, 59, 59, 999)
      
      emit('event-create', {
        start: startDate,
        end: endDate,
        allDay: true
      })
    }

    // 슬롯 마우스다운 핸들러
    const handleSlotMouseDown = (e, date, slotIndex) => {
      e.preventDefault()
      e.stopPropagation()
      
      const clickedSlot = e.currentTarget
      const actualSlotIndex = parseInt(clickedSlot.dataset.slotIndex) || slotIndex
      const actualDateIndex = parseInt(clickedSlot.dataset.dateIndex) || 0
      
      dragState.value = {
        active: true,
        type: 'create',
        startSlotIndex: actualSlotIndex,
        endSlotIndex: actualSlotIndex,
        dateIndex: actualDateIndex,
        originalEvent: null,
        duration: 0,
        originalDateIndex: actualDateIndex,
        originStartSlot: actualSlotIndex,
        clickOffset: 0
      }

      document.addEventListener('mousemove', handleDocumentMouseMove, true)
      document.addEventListener('mouseup', handleDocumentMouseUp, true)
    }

    // 이벤트 마우스다운 핸들러
    const handleEventMouseDown = (e, event) => {
      e.preventDefault()
      e.stopPropagation()
      
      const startX = e.clientX
      const startY = e.clientY
      let isDragging = false
      
      selectedEventId.value = event.id
      
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      const startSlot = timeToSlot(eventStart.getHours(), eventStart.getMinutes())
      // 끝은 올림이다. 10:15 에 끝나는 블록은 10:00~10:30 칸까지 덮어야 미리보기가 짧아지지 않는다.
      const endSlot = Math.max(
        startSlot,
        Math.ceil((eventEnd.getHours() * 60 + eventEnd.getMinutes()) / 30) - 1
      )
      const duration = endSlot - startSlot + 1
      
      const clickPosition = getGridPosition(e.clientX, e.clientY)
      const clickOffset = clickPosition.slotIndex - startSlot

      /*
       * 옮길 권한이 없으면 **끌리지 않게** 한다.
       * 예전에는 누구나 끌 수 있었고 미리보기까지 따라왔는데, 손을 떼면
       * Calendar.onGridUpdate 가 canEdit 을 보고 조용히 무시했다. 화면에서는
       * 옮겨진 것처럼 보이다가 다음 렌더에 제자리로 돌아가는, 되는 것처럼
       * 보이는데 안 되는 조작이었다.
       * canEdit 이 아예 없는 이벤트(다른 호출자)는 지금까지처럼 끌 수 있다 —
       * false 로 명시된 것만 막는다.
       */
      const canMove = itemOf(event).canEdit !== false

      const handleMove = (e) => {
        const distance = Math.sqrt(
          Math.pow(e.clientX - startX, 2) + 
          Math.pow(e.clientY - startY, 2)
        )
        
        if (!isDragging && canMove && distance > 5) {
          isDragging = true
          
          const eventDateIndex = dates.value.findIndex(d => 
            d.toDateString() === eventStart.toDateString()
          )
          
          dragState.value = {
            active: true,
            type: 'move',
            startSlotIndex: startSlot,
            endSlotIndex: endSlot,
            dateIndex: eventDateIndex,
            originalEvent: event,
            duration: duration,
            originalDateIndex: eventDateIndex,
            originStartSlot: startSlot,
            clickOffset: clickOffset
          }
        }
        
        if (isDragging) {
          handleDocumentMouseMove(e)
        }
      }

      /*
       * 끌지 않았으면 누른 시간과 상관없이 클릭이다.
       * 예전에는 300ms 안에 떼야만 클릭으로 쳐서, 천천히 누르면(터치패드·노년 사용자·
       * 폰에서 길게 탭) 아무 일도 일어나지 않았다 — 블록을 눌렀는데 열리지 않는 증상.
       */
      const handleUp = (e) => {
        if (!isDragging) {
          emit('event-click', event)
        } else {
          handleDocumentMouseUp(e)
        }
        
        document.removeEventListener('mousemove', handleMove, true)
        document.removeEventListener('mouseup', handleUp, true)
      }

      document.addEventListener('mousemove', handleMove, true)
      document.addEventListener('mouseup', handleUp, true)
    }

    // 문서 마우스무브 핸들러
    const handleDocumentMouseMove = (e) => {
      if (!dragState.value.active) return
      
      e.preventDefault()
      
      const currentPosition = getGridPosition(e.clientX, e.clientY)
      
      if (currentPosition.slotIndex === -1 || currentPosition.dateIndex === -1) {
        return
      }
      
      if (dragState.value.type === 'create') {
        // 누른 칸(originStartSlot)을 고정점으로 잡는다. 예전에는 직전 범위의 시작을 기준으로
        // 삼아서, 위로 끌었다가 다시 아래로 내리면 처음 누른 칸 위쪽이 범위에 남았다.
        const anchor = dragState.value.originStartSlot
        const startSlot = Math.min(anchor, currentPosition.slotIndex)
        const endSlot = Math.max(anchor, currentPosition.slotIndex)
        
        dragState.value.startSlotIndex = startSlot
        dragState.value.endSlotIndex = endSlot
        dragState.value.dateIndex = currentPosition.dateIndex
        
      } else if (dragState.value.type === 'move') {
        const duration = dragState.value.duration
        const clickOffset = dragState.value.clickOffset
        
        const newStartSlot = Math.max(0, Math.min(47 - duration, currentPosition.slotIndex - clickOffset))
        
        dragState.value.startSlotIndex = newStartSlot
        dragState.value.endSlotIndex = newStartSlot + duration - 1
        dragState.value.dateIndex = currentPosition.dateIndex
      }
    }

    // 문서 마우스업 핸들러
    const handleDocumentMouseUp = (e) => {
      if (!dragState.value.active) {
        document.removeEventListener('mousemove', handleDocumentMouseMove, true)
        document.removeEventListener('mouseup', handleDocumentMouseUp, true)
        return
      }
      
      e.preventDefault()
      
      const state = dragState.value
      const selectedDate = dates.value[state.dateIndex]
      
      if (!selectedDate) {
        resetDragState()
        return
      }
      
      const startTime = slotToTime(state.startSlotIndex)
      const endTime = slotToTime(state.endSlotIndex + 1)
      
      const startDate = new Date(selectedDate)
      startDate.setHours(startTime.hour, startTime.minute, 0, 0)
      
      const endDate = new Date(selectedDate)
      endDate.setHours(endTime.hour, endTime.minute, 0, 0)
      
      if (state.type === 'create') {
        emit('event-create', {
          start: startDate,
          end: endDate,
          allDay: false
        })
        
      } else if (state.type === 'move' && state.originalEvent) {
        /*
         * 슬롯으로 다시 만들지 않고 **원래 시각을 옮긴 만큼만 민다.**
         * 예전에는 놓은 슬롯에서 시작·끝을 새로 만들어서
         *   · 09:15~10:15 회의가 30분 단위로 잘려 09:00~10:00 이 되고(길이·분 손실)
         *   · 여러 날 걸친 일정의 하루 조각을 끌면 그 조각(00:00~23:59)이 일정 전체를
         *     대신해 사흘짜리 출장이 하루짜리로 줄어들었다
         *   · 제자리에 놓아도 저장 요청과 '옮겼습니다' 토스트가 나갔다
         */
        const ev = state.originalEvent
        const slotDelta = state.startSlotIndex - state.originStartSlot
        const dayDelta = state.dateIndex - state.originalDateIndex
        if (slotDelta || dayDelta) {
          const shift = (v) => {
            const d = new Date(v)
            d.setDate(d.getDate() + dayDelta)
            d.setMinutes(d.getMinutes() + slotDelta * 30)
            return d
          }
          emit('event-update', {
            id: ev.id,
            start: shift(ev.originalStart || ev.start),
            end: shift(ev.originalEnd || ev.end)
          })
        }
      }
      resetDragState()
    }

    const resetDragState = () => {
      dragState.value = IDLE_DRAG()

      document.removeEventListener('mousemove', handleDocumentMouseMove, true)
      document.removeEventListener('mouseup', handleDocumentMouseUp, true)
    }

    // 현재 시간 업데이트
    const updateCurrentTime = () => {
      currentTime.value = new Date()
    }

    const isSameDay = (date1, date2) => {
      if (!date1 || !date2) return false
      return date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear()
    }

    function scrollToMorning() {
      const wrap = scrollWrapper.value
      if (!wrap || !gridBody.value) return
      const pinned = metrics.value.header + (allDayTall.value ? 0 : getAllDaySectionHeight())
      wrap.scrollTop = Math.max(0, gridBody.value.offsetTop + metrics.value.slot * 16 - pinned)
    }

    /*
     * 캘린더로 곧장 들어오면 그리드가 빈 채로 먼저 마운트되고 항목은 조금 뒤에 온다.
     * 그 사이 종일 밴드가 자라면(브라우저 스크롤 앵커링이 끼어들어) 08:00 이 한 시간쯤
     * 아래로 밀린다. 첫 데이터가 그려진 뒤 한 번 더 맞춘다 — 사용자가 이미 스크롤했으면
     * 손대지 않는다.
     */
    let touchedByUser = false
    const stopFirstData = props.events.length
      ? null // 이미 그려진 채 마운트됐다(뷰 전환). onMounted 의 한 번이면 된다.
      : watch(
        () => props.events.length,
        (n) => {
          if (!n) return
          stopFirstData()
          if (!touchedByUser) nextTick(scrollToMorning)
        },
        { flush: 'post' }
      )

    onMounted(() => {
      updateCurrentTime()
      const markTouched = () => { touchedByUser = true }
      scrollWrapper.value?.addEventListener('wheel', markTouched, { passive: true, once: true })
      scrollWrapper.value?.addEventListener('touchstart', markTouched, { passive: true, once: true })

      /*
       * 처음 열었을 때 08:00 이 화면 맨 위에 오게 내린다.
       * 하루가 1248px(모바일) ~ 1440px 이라 0시에서 시작하면 새벽 다섯 시간이
       * 화면을 다 먹고, 실제 업무 시간은 사용자가 매번 손으로 찾아 내려야 했다.
       *
       * 08:00 = 30분 슬롯 16칸. **metrics 에서 뽑는다** — 여기에 60*8 같은
       * 리터럴을 적으면 모바일(26px 슬롯)에서 10:24 로 가 버린다.
       * 렌더가 끝나야 스크롤 높이가 생기므로 nextTick 뒤에 준다.
       *
       * 시간 본문 위에는 날짜 헤더와 종일 밴드가 있다. 08:00 줄의 실제 위치(본문 offsetTop +
       * 16칸)에서 **화면 위에 붙어 있는 높이**(헤더, 그리고 밴드가 sticky 일 때 밴드)를 빼야
       * 08:00 이 그 밑에 딱 보인다. 16칸만 내리면 밴드 높이만큼 위(새벽)가 보였다.
       */
      nextTick(scrollToMorning)

      const interval = setInterval(updateCurrentTime, 60000)

      onUnmounted(() => {
        clearInterval(interval)
        resetDragState()
      })
    })

    return {
      gridVars,
      timeGridContainer,
      scrollWrapper,
      allDaySection,
      gridBody,
      dateColumnsRef,
      hours,
      timeSlots,
      dates,
      currentTimePosition,
      nowLabel,
      showsToday,
      itemOf,
      userOf,
      locationOf,
      isPast,
      selectedEventId,
      dragState,
      dragOverlayStyle,
      dragOverlayText,
      isToday,
      isSaturday,
      isSunday,
      isHoliday,
      getHolidayName,
      getEventLayouts,
      formatDate,
      formatDay,
      formatHour,
      formatEventTime,
      formatEventDateRange,
      getUserProfileImage,
      handleSlotMouseDown,
      handleEventMouseDown,
      handleAllDayClick,
      handleAllDayEventClick,
      visibleAllDayEvents,
      allDayRowCount,
      COLLAPSED_ROWS,
      allDayCollapsed,
      allDayTall,
      hiddenAllDayRows,
      toggleAllDay
    }
  }
}
</script>


<style scoped>
/*
 * 기하 변수(--tg-slot-h / --tg-gutter / --tg-header-h / --tg-day-h)는
 * script 의 metrics 가 :style 로 내려준다. **여기에 같은 숫자를 다시 적지 않는다** —
 * 적는 순간 JS 가 보는 값과 갈라져서 드래그로 그은 시각과 실제로 만들어지는
 * 일정이 어긋난다.
 *
 * 아래에서 새로 정의하는 건 JS 가 모르는 값 하나뿐이다: --tg-allday-row.
 * 종일 밴드의 높이는 JS 가 계산하지 않고 offsetHeight 로 재기 때문에
 * (getAllDaySectionHeight) CSS 가 정해도 두 쪽이 갈라지지 않는다.
 */
.custom-time-grid {
  --tg-allday-row: 24px;

  /*
   * 그리드 안 층위. --z-* 토큰(앱 크롬)과 다른 축이다 — 이 값들은 전부
   * .custom-time-grid 안에서만 겨루므로 전역 스케일에 섞지 않는다.
   * 숫자를 흩어 적어 두면 새 요소를 얹을 때 어디에 끼울지 알 수 없다.
   *
   * 이 묶음은 예전에 모바일 미디어쿼리 **안에** 들어가 있었다. 그래서 데스크톱에서는
   * 변수가 전부 미정의 → z-index 가 auto 로 떨어졌고, 스크롤하면 시간 블록이
   * 고정된 날짜 헤더·종일 밴드 **위로** 지나가 헤더를 덮었다.
   */
  --tg-z-block: 10;          /* 시간 블록 기본(인라인 style 이 이 변수를 그대로 쓴다) */
  --tg-z-block-selected: 12;
  --tg-z-block-hover: 14;
  --tg-z-now: 16;            /* 현재 시각 선 — 블록 위, 고정 헤더 아래 */
  --tg-z-gutter: 17;         /* 좌측 시각 눈금(가로 스크롤 시 고정). 블록·현재선 위 — 같은 10 이던 때는
                                태블릿에서 주 뷰를 옆으로 밀면 블록이 시각 눈금을 덮었다 */
  --tg-z-allday: 19;         /* 종일 밴드(세로 스크롤 시 고정) */
  --tg-z-header: 20;         /* 날짜 헤더 — 항상 맨 위 */
  --tg-z-drag: 30;           /* 끌고 있는 블록의 오버레이 */

  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  /* 월 그리드(UnifiedMonthView)와 같은 처리: 카드로 띄우지 않고 캔버스에
     붙인 뒤 위쪽 경계선만 긋는다. 두 뷰를 오갈 때 프레임이 나타났다
     사라지지 않아야 한다. */
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  font-size: var(--fs-md);
  color: var(--color-text);
}

.time-grid-wrapper {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
}

/* ==================================================================
   헤더 (날짜 줄)
   요일 · 날짜 원 · 공휴일 이름. 월 그리드와 같은 어휘를 쓴다 —
   오늘은 파란 원, 일요일 --cal-sun, 토요일 --cal-sat.
   ================================================================== */
.time-header {
  display: flex;
  /*
   * 높이를 **고정**한다. min-height 로 두면 공휴일 이름이 있는 주에만 헤더가
   * 자라는데, 드래그 오버레이의 top 은 metrics.header 를 상수로 더해서 잡기
   * 때문에 자란 만큼 통째로 어긋난다.
   */
  height: var(--tg-header-h);
  flex-shrink: 0;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: var(--tg-z-header);
}

.week-view-mode .time-header {
  min-width: calc(var(--tg-gutter) + 150px * 7);
}

.time-label-column {
  width: var(--tg-gutter);
  min-width: var(--tg-gutter);
  background: var(--color-surface-3);
  border-right: 1px solid var(--color-border);
}

.date-columns-header {
  flex: 1;
  display: flex;
  min-width: 0;
}

.date-column {
  flex: 1;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 4px;
  text-align: center;
  border-right: 1px solid var(--color-border);
  overflow: hidden;
}

.date-column:last-child {
  border-right: none;
}

/* 헤더 칸을 누르면 그 날의 종일 일정을 만든다(handleAllDayClick).
   이 뷰에서 종일 항목을 만드는 유일한 경로라 커서와 호버를 분명히 준다. */
.date-column-clickable {
  cursor: pointer;
  transition: background-color var(--dur-fast) var(--ease);
}

.date-dow {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.04em;
  line-height: 1.2;
  color: var(--color-text-muted);
}
.date-column.is-sunday .date-dow,
.date-column.is-holiday .date-dow { color: var(--cal-sun); }
.date-column.is-saturday .date-dow { color: var(--cal-sat); }

.date-line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 0;
}

/* 달이 바뀌는 자리에만 붙는 작은 월 표시 */
.date-mon {
  font-size: var(--fs-2xs);
  font-weight: var(--fw-medium);
  color: var(--color-text-subtle);
  flex-shrink: 0;
}

.date-num {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-full);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  line-height: 1;
  color: var(--color-text);
}
.date-column.is-sunday .date-num,
.date-column.is-holiday .date-num { color: var(--color-danger); }
.date-column.is-saturday .date-num { color: var(--color-primary); }
/* 오늘 원은 주말 색을 이겨야 하므로 뒤에 온다 */
.date-column.is-today .date-num { background: var(--color-primary); color: var(--color-on-accent); }

.holiday-name {
  max-width: 100%;
  font-size: var(--fs-3xs);
  line-height: 1.1;
  color: var(--cal-sun);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 오늘 컬럼은 머리부터 발끝까지 아주 옅게 깐다(월 그리드와 같은 토큰).
   .is-other-month 가 없는 뷰라 순서 다툼은 호버 하나뿐이다. */
.date-column.is-today { background: var(--cal-today-bg); }

@media (hover: hover) {
  .date-column-clickable:hover { background: var(--color-surface-2); }
}

/* ==================================================================
   종일 밴드
   ================================================================== */
.all-day-section {
  display: flex;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: var(--tg-header-h);
  z-index: var(--tg-z-allday);
}

/*
 * 줄이 많아지면 고정을 푼다.
 * 헤더 밑에 붙어 있는 sticky 블록이 화면 절반을 넘어가면 시간 그리드가 설 자리가
 * 없어서, 밴드를 접기 전에는 아무것도 볼 수 없게 된다. 그 지점부터는 밴드가
 * 본문과 함께 흘러가는 편이 낫다 — 위로 올리면 다시 보인다.
 * (기준 줄 수는 script 의 STICKY_MAX_ROWS. 데스크톱 8줄 · 모바일 3줄)
 */
.all-day-section.is-tall {
  position: static;
}

.week-view-mode .all-day-section {
  min-width: calc(var(--tg-gutter) + 150px * 7);
}

.all-day-label {
  width: var(--tg-gutter);
  min-width: var(--tg-gutter);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  padding: 4px 2px;
  font-size: var(--fs-2xs);
  font-weight: var(--fw-medium);
  color: var(--color-text-muted);
  background: var(--color-surface-3);
  border-right: 1px solid var(--color-border);
}

/* 라벨과 토글이 세로로 쌓이므로 첫 줄 높이를 한 행에 맞춘다 */
.all-day-label__text {
  line-height: var(--tg-allday-row);
}

/* 시각 크기는 작게 두고 히트 영역만 넓힌다. 조상(.all-day-label)에
   overflow 가 없어 잘리지 않는다. */
.all-day-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 3px;
  border: 0;
  border-radius: var(--r-xs);
  background: none;
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--fs-3xs);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.all-day-toggle::after { content: ''; position: absolute; inset: -10px -6px; }
.all-day-toggle:hover { color: var(--color-primary); }

.all-day-toggle__more { color: var(--color-primary); }

.all-day-toggle__caret {
  transition: transform var(--dur-fast) var(--ease);
}
.all-day-toggle__caret.is-open { transform: rotate(180deg); }

@media (prefers-reduced-motion: reduce) {
  .all-day-toggle__caret { transition: none; }
}

.all-day-events-container {
  flex: 1;
  position: relative;
  min-width: 0;
  /*
   * **안쪽 스크롤을 두지 않는다.**
   *
   * 예전에는 `max-height: 30vh; overflow-y: auto` 였다. 이 조직의 실제 데이터에서
   * 종일 밴드는 한 주에 중앙값 14줄 · 최대 24줄이 나오는데 30vh 는 대략 9줄이라,
   * 평소에도 3분의 1이 작은 스크롤 상자 뒤에 숨어 있었다. 페이지 스크롤과
   * 밴드 스크롤이 겹쳐서, 숨은 줄이 있다는 것조차 알기 어려웠다.
   *
   * 이제 줄 수만큼 그대로 자란다. 밴드가 화면을 먹는 문제는 접기 토글과
   * .is-tall(고정 해제)로 푼다 — 잘라서 숨기는 대신 사용자가 정한다.
   *
   * 여기 overflow 가 사라지면서 안쪽 칩의 ::after 히트 영역도 더는 잘리지 않는다.
   */
}

/*
 * 통합 종일 레이어. 행은 "사람" 단위로 잡히고(allDayLayout),
 * 여러 날 걸친 항목은 grid-column 으로 이어 붙는다.
 * 컨테이너는 포인터를 받지 않는다 — 뒤의 헤더 칸(종일 추가)이 살아 있어야 한다.
 */
.all-day-events-grid {
  display: grid;
  grid-template-rows: var(--tg-allday-row);
  grid-auto-rows: var(--tg-allday-row);
  gap: 2px 0;
  padding: 2px 0;
  width: 100%;
  pointer-events: none;
}

.all-day-events-grid.week-view {
  grid-template-columns: repeat(7, minmax(150px, 1fr));
}

.all-day-events-grid.day-view {
  grid-template-columns: minmax(150px, 1fr);
}

/* ==================================================================
   본체 : 시각 거터 · 컬럼 · 30분 슬롯
   ================================================================== */
.time-grid-body {
  display: flex;
  position: relative;
}

.week-view-mode .time-grid-body {
  min-width: calc(var(--tg-gutter) + 150px * 7);
}

.time-labels {
  width: var(--tg-gutter);
  min-width: var(--tg-gutter);
  background: var(--color-surface-3);
  border-right: 1px solid var(--color-border);
  position: sticky;
  left: 0;
  z-index: var(--tg-z-gutter);
}

.time-label-wrapper {
  height: calc(var(--tg-slot-h) * 2);
  position: relative;
  border-bottom: 1px solid var(--color-border);
}

.time-label-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: var(--fs-xs);
  font-weight: var(--fw-medium);
  color: var(--color-text-subtle);
  white-space: nowrap;
}

.date-columns {
  flex: 1;
  display: flex;
  position: relative;
  min-width: 0;
}

.date-column-body {
  flex: 1;
  min-width: 150px;
  position: relative;
  border-right: 1px solid var(--color-border);
}

.date-column-body:last-child {
  border-right: none;
}

/*
 * 오늘 컬럼만 칠한다. 예전에는 토/일/공휴일 컬럼까지 파랑·빨강 그라디언트를
 * 깔았는데(EP 기본 팔레트 잔재), 그 위에 유형색 블록이 얹히면 색이 서로 싸웠다.
 * 주말은 월 그리드와 같이 **헤더 글자색**으로만 말한다.
 */
.date-column-body.is-today { background: var(--cal-today-bg); }

.time-slots-container {
  position: relative;
  height: var(--tg-day-h);
}

.time-slot {
  height: var(--tg-slot-h);
  position: relative;
  cursor: pointer;
  /* 30분 자리는 거의 안 보이는 선으로만 — 가로줄이 진하면 그리드가
     일정보다 먼저 읽힌다 */
  border-bottom: 1px solid var(--color-surface-2);
  transition: background-color var(--dur-fast) var(--ease);
}

/* 정시 경계(슬롯 두 칸마다)만 실선 */
.time-slot:nth-child(even) {
  border-bottom: 1px solid var(--color-border);
}

@media (hover: hover) {
  .time-slot:hover { background: var(--color-surface-2); }
}

/*
 * 30분 구분선은 바로 위 슬롯의 border-bottom 과 **같은 자리**에 겹쳐 그려지고
 * 있었다(색까지 같아서 보이지도 않았다). 마크업은 남기고 그리지만 않는다 —
 * 지우면 slotIndex % 2 를 쓰는 템플릿 조건까지 건드려야 한다.
 */
.slot-divider { display: none; }

.events-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--tg-day-h);
  pointer-events: none;
}

/* ==================================================================
   일정 / 업무 블록

   월 그리드와 같은 규칙이다:
     · 일정 = 유형색으로 꽉 찬 칩 (흰 글자)
     · 업무 = 옅은 배경 + 좌측 상태색 띠 + 체크박스
   같은 그리드 안에서 "일정인가 업무인가" 가 형태만으로 구분돼야 한다.
   ================================================================== */
.event-block {
  position: relative;
  min-width: 0;
  padding: 2px 5px;
  /* 좌측 3px 은 전 종류가 공유하는 자리다. 색이 없는 종류에서도 투명으로
     잡아 둬야 제목의 시작 x 가 블록마다 흔들리지 않는다. */
  border: 1px solid transparent;
  border-left: 3px solid transparent;
  border-radius: var(--r-sm);
  overflow: hidden;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: var(--sh-1);
  transition: filter var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
}

/*
 * 잡을 수 있는 블록만 손 모양이 된다 — 권한이 없으면 끌리지 않는다는 신호다
 * (월 그리드의 칩과 같은 규칙). 끄는 동안에는 그리드 전체가 grabbing 이어야
 * 하는데, 블록·슬롯이 각자 cursor 를 갖고 있어 body 커서로는 안 먹는다.
 */
.event-block.is-draggable { cursor: grab; }
.custom-time-grid.is-dragging .event-block.is-draggable,
.custom-time-grid.is-dragging .time-slot { cursor: grabbing; }
/* 끌고 있는 동안의 호버 밝기 변화는 방해만 된다 */
.custom-time-grid.is-dragging .event-block:hover { filter: none; }

.event-content {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.event-title-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.event-title {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-time {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: var(--fs-2xs);
  line-height: 1.3;
  opacity: 0.9;
}

.event-time-text { flex-shrink: 0; font-weight: var(--fw-medium); }

/* 그려서 만드는 체크박스. 글리프(☑/☐)는 OS·폰트마다 크기가 달라
   11.5px 제목의 기준선이 항목마다 흔들린다(월 그리드와 같은 이유). */
.event-check {
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border: 1.4px solid currentColor;
  border-radius: var(--r-xs);
  opacity: 0.75;
}
.event-check.is-done { background: currentColor; opacity: 0.9; }

.location-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  opacity: 0.85;
}

.location-icon { display: inline-flex; flex-shrink: 0; }

.location-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-user {
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  font-size: var(--fs-2xs);
  opacity: 0.9;
}

.user-avatar {
  width: 14px;
  height: 14px;
  border-radius: var(--r-full);
  object-fit: cover;
  flex-shrink: 0;
}

.user-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------- 일정: 유형색 채우기 ----------
   기본값을 업무색으로 둔다. 유형이 비어 오는 데이터가 있어서, 없으면
   배경 없는 블록에 흰 글자가 찍혀 통째로 사라진다. */
.event-block.is-schedule { background: var(--evt-work); color: var(--color-on-accent); }
.event-block.is-schedule.event-work { background: var(--evt-work); }
.event-block.is-schedule.event-substitute { background: var(--evt-substitute); }
.event-block.is-schedule.event-dayoff { background: var(--evt-dayoff); }
.event-block.is-schedule.event-vacation { background: var(--evt-vacation); }

/*
 * 외근(보라)·점검(주황)만 다르게 간다. 이 둘은 원색이 가장 진해서 두 시간짜리
 * 블록을 통째로 칠하면 화면을 때린다. 토큰에 옅은 배경 + 진한 글자 쌍이
 * 있는 것도 이 둘뿐이라, 배경은 -bg / 글자는 -fg / 유형색은 좌측 3px 바로 남긴다.
 */
.event-block.is-schedule.event-fieldwork {
  background: var(--evt-fieldwork-bg);
  color: var(--evt-fieldwork-fg);
  border-left-color: var(--evt-fieldwork);
}
.event-block.is-schedule.event-inspection {
  background: var(--evt-inspection-bg);
  color: var(--evt-inspection-fg);
  border-left-color: var(--evt-inspection);
}

/* ---------- 업무: 옅은 배경 + 좌측 상태색 띠 ---------- */
.event-block.is-task {
  background: var(--color-surface-2);
  color: var(--color-text);
  border-color: var(--color-border);
  border-left-color: var(--task-pending);
}
.event-block.is-task .event-time,
.event-block.is-task .event-user { color: var(--color-text-muted); opacity: 1; }

/* 상태값은 taskStatus.js 의 문자열 그대로다(in_progress 는 밑줄, 토큰은 하이픈) */
.event-block.is-task.task-pending { border-left-color: var(--task-pending); }
.event-block.is-task.task-in_progress { border-left-color: var(--task-in-progress); }
.event-block.is-task.task-done { border-left-color: var(--task-done); }
.event-block.is-task.task-done .event-title {
  color: var(--color-text-subtle);
  text-decoration: line-through;
}

/* 이미 끝난 항목 */
.event-block.is-past { opacity: 0.62; }

/* ---------- 종일 칩 ----------
   월 그리드의 스팬 칩과 같은 모양: 한 줄, 24px, radius 4px.
   .event-block 과 특이도가 같아 순서에 기대지 않도록 둘을 함께 잡는다. */
.all-day-event.event-block {
  display: flex;
  align-items: center;
  gap: 4px;
  height: var(--tg-allday-row);
  margin: 0 2px;
  padding: 0 5px;
  /* UnifiedMonthView 의 .umv__chip 과 같은 값(--r-xs 3px). 4px 로 두면
     같은 성격의 칩이 화면마다 다른 모서리를 갖는다. */
  border-radius: var(--r-xs);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: 1;
  box-shadow: none;
  pointer-events: auto;
}

.all-day-event .event-title { flex: 1; }
.all-day-event .event-date-range {
  flex-shrink: 0;
  font-size: var(--fs-2xs);
  font-weight: var(--fw-medium);
  opacity: 0.85;
}
.all-day-event .location-badge,
.all-day-event .event-user {
  flex-shrink: 0;
  max-width: 40%;
  font-size: var(--fs-2xs);
  font-weight: var(--fw-medium);
}
.all-day-event .user-name { max-width: 56px; }

/* 여러 날 걸친 칩은 이어지는 쪽 모서리를 붙여 "계속됨" 을 형태로 말한다 */
.all-day-event.is-multi-day:not(.is-start) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: 0;
  border-left-width: 0;
}

.all-day-event.is-multi-day:not(.is-end) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-right: 0;
}

/*
 * 블록은 전부 --tg-z-allday(19) 아래에 둔다. 스크롤하면 헤더와 종일 밴드가
 * 위에 고정되는데, 예전에는 호버가 20 !important 라 블록이 헤더 위로 튀어나왔다.
 * (인라인 style 의 zIndex 를 이기려면 !important 가 필요하다)
 */
@media (hover: hover) {
  .event-block:hover {
    filter: brightness(0.96);
    box-shadow: var(--sh-2);
    z-index: var(--tg-z-block-hover) !important;
  }
}

.event-block.is-selected {
  box-shadow: 0 0 0 2px var(--color-primary);
  z-index: var(--tg-z-block-selected) !important;
}

/* ==================================================================
   현재 시각
   거터의 숫자 + 오늘 컬럼의 점 + 선. 홈·월 그리드와 같은 모양.
   ================================================================== */
.now-label {
  position: absolute;
  right: 4px;
  transform: translateY(-50%);
  padding: 0 3px;
  border-radius: var(--r-xs);
  background: var(--color-surface-3);
  font-size: var(--fs-2xs);
  font-weight: var(--fw-bold);
  line-height: 1.5;
  color: var(--color-danger);
  white-space: nowrap;
  pointer-events: none;
  z-index: 2;
}

.current-time-indicator {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: var(--tg-z-now);
}

.current-time-line {
  height: 2px;
  background: var(--color-danger);
  border-radius: var(--r-full);
}

.current-time-dot {
  position: absolute;
  left: -4px;
  top: -3px;
  width: 8px;
  height: 8px;
  border-radius: var(--r-full);
  background: var(--color-danger);
}

/* ==================================================================
   드래그 미리보기
   ================================================================== */
.drag-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary-soft);
  border: 1.5px solid var(--color-primary);
  border-radius: var(--r-sm);
  pointer-events: none;
  overflow: hidden;
}

.drag-overlay-content {
  padding: 2px var(--sp-2);
  border-radius: var(--r-full);
  background: var(--color-primary);
  color: var(--color-on-accent);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  white-space: nowrap;
}

/* ------------------------------------------------------------------
   모바일 (≤767px)
   주 뷰는 모바일에서 노출하지 않는다(Calendar.vue 의 availableViews).
   여기서 다루는 건 **일 뷰** 하나다.

   기하(슬롯 · 시각 거터 · 헤더 높이)는 script 의 metrics 하나가 정하고
   CSS 는 --tg-* 변수로 받아 쓴다. 그래서 여기서는 폭에 따라 달라지는 값을
   따로 적지 않는다 — 적으면 JS 가 보는 숫자와 갈라진다.
   여기 남는 것은 기하와 무관한 것들뿐이다: 글자 크기, 여백, 종일 칩의 탭 영역.
   ------------------------------------------------------------------ */
@media (max-width: 767px) { /* --bp-mobile-max */
  .time-grid-wrapper {
    -webkit-overflow-scrolling: touch;
    /* 일 뷰는 컬럼이 하나뿐이라 가로로 흐를 이유가 없다.
       스크롤이 생기면 세로 스크롤 중에 화면이 옆으로 새어 나간다. */
    overflow-x: hidden;
    overscroll-behavior-y: contain;
  }

  /* 일 뷰에서는 컬럼이 하나이므로 150px 최소폭이 필요 없다.
     남기면 좁은 기기에서 가로 스크롤이 생긴다. */
  .date-column,
  .date-column-body {
    min-width: 0;
  }
  .all-day-events-grid.day-view { grid-template-columns: minmax(0, 1fr); }

  .date-column { padding: 3px 4px; }
  .date-dow { font-size: var(--fs-xs); }
  .time-label-text { font-size: var(--fs-xs); }

  /*
   * 종일 칩의 탭 대상은 **행 자체를 44px 로 올려서** 만든다.
   *
   * ::after 로 히트 영역만 넓히는 수법(StatusPill 선례)을 여기서는 쓸 수 없다:
   * 행 간격이 2px 뿐이라 위아래 6px 씩 나간 띠가 이웃 행과 10px 겹치고,
   * 겹친 자리에서는 엉뚱한 항목이 열린다. StatusPill 의 주석이 "두 사용처 모두
   * gap 이 8px 이상이라 이웃을 안 덮는다" 를 계산해 둔 것처럼, 그 계산이
   * 성립하지 않으면 이 수법을 쓰면 안 된다.
   *
   * (예전에는 컨테이너의 overflow-y:auto 조상 클리핑도 이유였다. 그건
   *  안쪽 스크롤을 없애면서 사라졌지만, 행 간격 문제는 그대로라 결론은 같다.)
   *
   * 44px 행이 밴드를 키우는 문제는 접기 토글과 .is-tall 로 푼다 — 잘라서
   * 숨기지 않는다.
   */
  .custom-time-grid { --tg-allday-row: var(--tap-min); }
  .all-day-event.event-block {
    font-size: var(--fs-xs);
    padding: 0 7px;
  }
  .all-day-event .user-name { max-width: 60px; }
  .all-day-label { font-size: var(--fs-2xs); }

  /*
   * 시간 슬롯(26px)은 히트 영역을 넓히지 **않는다.**
   * 그리드는 빈틈 없는 면이라 "빗나감" 이 없고, 슬롯끼리 ::after 를 겹치면
   * 손끝이 옆 칸을 잡아 30분 어긋난 일정이 조용히 만들어진다.
   * 작아서 못 누르는 문제가 아니라 정확도 문제라 확장이 답이 아니다.
   */

  /* 일정 블록: 30분(26px)짜리도 제목 한 줄은 읽혀야 한다 */
  .event-block { padding: 2px 5px; }
  .event-block .event-title { font-size: var(--fs-xs); line-height: 1.25; }
}
</style>
